import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../Contexts/UserContext';
import PermissionDenied from '../Components/PermissionDenied.tsx';
import { useSchool } from '../Contexts/SchoolContext.tsx';
import { Roles } from '../lib/constants.ts';
import FeatureLocked from '../Components/FeatureLocked.tsx';
import Loader from '../Components/Loader.tsx';

interface ProtectedRouteProps {
  children: JSX.Element | React.ReactNode;
  roles?: string[];
  feature?: string;
  message?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  roles,
  feature,
  message,
}) => {
  const { user, hasPermissions } = useUser();
  const location = useLocation();
  const { schoolDetail, isLoading } = useSchool();

  if (isLoading) {
    return <Loader color="fill-[#DDE7FF]" />;
  }

  if (!user) {
    if (location.pathname.includes('parent')) {
      return <Navigate to="/parent/login" state={{ from: location }} replace />;
    } else {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  }

  const hasFeature = feature ? schoolDetail?.features?.includes(feature) : true;

  if (!hasFeature) {
    return <FeatureLocked message={message} showContactButton={false} />;
  }

  if (roles && !hasPermissions(roles)) {
    // Create a user-friendly message based on the required roles
    const roleMessages: Record<string, string> = {
      [Roles.SCHOOL_ADMIN]: 'administrator',
      [Roles.TEACHER]: 'teacher',
      [Roles.STAFF_MANAGER]: 'staff manager',
      [Roles.STUDENT_MANAGER]: 'student manager',
      [Roles.FEES_COLLECTION]: 'fees collection staff',
      [Roles.VIEW_FINANCIAL_REPORTS]: 'financial reports viewer',
      [Roles.MANAGE_PAYROLL]: 'payroll manager',
      [Roles.ATTENDANCE_MANAGER]: 'attendance manager',
      [Roles.STUDENT]: 'student',
      [Roles.ACADEMICS_MANAGER]: 'academics manager',
    };

    const requiredRoleNames = roles
      .map(role => roleMessages[role] || role.toLowerCase().replace(/_/g, ' '))
      .join(', ');

    const permissionMessage = `This page is designed for ${requiredRoleNames}. Your administrator can help you get the right permissions to access this feature!`;

    return (
      <PermissionDenied
        title="Permission Needed"
        message={message || permissionMessage}
        showBackButton={true}
        showHomeButton={hasPermissions([
          Roles.SCHOOL_ADMIN,
          Roles.VIEW_FINANCIAL_REPORTS,
          Roles.FEES_COLLECTION,
        ])}
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
